import { useCallback, useEffect, useState } from 'react';
import { MetaTags } from 'react-meta-tags';
import styled from 'styled-components';
import Logo from '../components/Logo/Logo';
import Header from '../components/Header/Header';
import Navigation from '../components/Navigation/Navigation';
import Footer from '../components/Footer/Footer';
import API from '../api';
import { clearLocalStorage, setLocalStorage } from '../utils';
import { RESPONSE_STATUSES } from '../constants';
import ScrollMessage from '../components/ScrollMessage/ScrollMessage';
import PublicHeader from '../screens/Home/comp/Header';
import PublicFooter from '../screens/Home/comp/Footer';

const StyledAuthMainOuter = styled.div`
  min-height: 100vh;
  width: 100%;

  background:
    radial-gradient(
      circle at 10% 10%,
      rgba(37, 99, 235, 0.09),
      transparent 30%
    ),
    radial-gradient(
      circle at 90% 90%,
      rgba(79, 70, 229, 0.08),
      transparent 30%
    ),
    linear-gradient(
      135deg,
      #f8fafc 0%,
      #f1f5f9 50%,
      #f8fafc 100%
    );

  display: flex;
  align-items: center;
  justify-content: center;

  padding: 24px;

  /* Subtle depth around the entire auth area */
  box-shadow:
    inset 0 0 80px rgba(15, 23, 42, 0.035),
    inset 0 1px 0 rgba(255, 255, 255, 0.8);

  @media (max-width: 768px) {
    padding: 0;
  }
`;

const StyledMainSection = styled.div`
  width: 100%;
  max-width: 1120px;
  min-height: 650px;

  display: grid;
  grid-template-columns: minmax(0, 0.95fr) minmax(0, 1.05fr);

  overflow: hidden;

  background: #ffffff;

  border: 1px solid #e2e7ef;
  border-radius: 18px;

  box-shadow:
    0 25px 70px rgba(15, 23, 42, 0.08),
    0 4px 12px rgba(15, 23, 42, 0.03);

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    max-width: 520px;
    min-height: auto;
  }

  @media (max-width: 768px) {
    border: none;
    border-radius: 0;
    box-shadow: none;
    min-height: 100vh;
  }
`;

const StyledAuthFormSection = styled.div`
  display: flex;
  flex-direction: column;

  background: #ffffff;

  padding: 15px 45px 35px;

  @media (max-width: 900px) {
    padding: 28px 35px 35px;
  }

  @media (max-width: 600px) {
    padding: 22px 20px 30px;
  }
`;

const StyledAuthHeader = styled.div`
  width: 100%;
  min-height: 45px;

  display: flex;
  align-items: center;

  margin-bottom: 10px;
`;

const StyledAuthFormIn = styled.div`
  width: 100%;
  max-width: 410px;

  margin: auto;

  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const StyledContentArea = styled.div`
  position: relative;

  display: flex;
  align-items: center;
  justify-content: center;

  padding: 55px;

  overflow: hidden;

  background:
    radial-gradient(
      circle at 15% 15%,
      rgba(37, 99, 235, 0.22),
      transparent 32%
    ),
    radial-gradient(
      circle at 90% 85%,
      rgba(79, 70, 229, 0.28),
      transparent 35%
    ),
    linear-gradient(
      145deg,
      #0b1220 0%,
      #101a31 50%,
      #111827 100%
    );

  color: #ffffff;

  @media (max-width: 900px) {
    display: none;
  }
`;

const StyledContentInner = styled.div`
  position: relative;
  z-index: 2;

  width: 100%;
  max-width: 400px;
`;

const StyledContentLogo = styled.div`
  width: 74px;
  height: 74px;

  display: flex;
  align-items: center;
  justify-content: center;

  margin-bottom: 25px;

  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 16px;

  background: rgba(255, 255, 255, 0.08);

  backdrop-filter: blur(10px);

  overflow: hidden;

  img {
    width: 58px;
    height: 58px;

    object-fit: contain;
  }
`;

const BrandText = styled.div`
  margin: 0;

  color: #ffffff;

  font-size: 32px;
  line-height: 1.15;
  font-weight: 800;

  letter-spacing: -0.03em;
`;

const BrandTagline = styled.div`
  margin-top: 9px;

  color: #8fa8ff;

  font-size: 10px;
  font-weight: 800;

  letter-spacing: 0.18em;
`;

const BrandDescription = styled.p`
  max-width: 360px;

  margin: 22px 0 0;

  color: rgba(255, 255, 255, 0.67);

  font-size: 14px;
  line-height: 1.75;
`;

const BrandLine = styled.div`
  width: 45px;
  height: 2px;

  margin: 27px 0 22px;

  border-radius: 2px;

  background: linear-gradient(
    90deg,
    #2563eb,
    #6366f1
  );
`;

const BrandFeatures = styled.div`
  display: flex;
  flex-direction: column;
  gap: 13px;
`;

const BrandFeature = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  color: rgba(255, 255, 255, 0.72);

  font-size: 12px;
  font-weight: 500;
`;

const FeatureDot = styled.span`
  width: 6px;
  height: 6px;

  flex-shrink: 0;

  border-radius: 50%;

  background: #5b7cff;

  box-shadow: 0 0 0 4px rgba(91, 124, 255, 0.1);
`;


// const widthAuthHOC = (Component) => (props) => {
//   const { appName, appLogo } = props.appData;

//   return (
//     <StyledAuthMainOuter>
//       <StyledMainSection>
//         <StyledAuthFormSection>
//           <StyledAuthHeader>
//             <Logo appName={appName} appLogo={appLogo} />
//           </StyledAuthHeader>
//           <StyledAuthFormIn>
//             <Component {...props} />
//           </StyledAuthFormIn>
//         </StyledAuthFormSection>
//         <StyledContentArea>
//           <img src={appLogo} alt={appName} />
//         </StyledContentArea>
//       </StyledMainSection>
//     </StyledAuthMainOuter>
//   );
// };
const widthAuthHOC = (Component) => (props) => {
  const { appName, appLogo } = props.appData;

  return (
    <StyledAuthMainOuter>
      <StyledMainSection>

        {/* =========================
            LEFT - AUTHENTICATION
        ========================== */}

        <StyledAuthFormSection>

          <StyledAuthHeader>
            <Logo
              appName={appName}
              appLogo={appLogo}
            />
          </StyledAuthHeader>

          <StyledAuthFormIn>
            <Component {...props} />
          </StyledAuthFormIn>

        </StyledAuthFormSection>


        {/* =========================
            RIGHT - BRAND AREA
        ========================== */}

        <StyledContentArea>

          <StyledContentInner>

            <StyledContentLogo>
              <img
                src={appLogo}
                alt={appName}
              />
            </StyledContentLogo>

            <BrandText>
              {appName}
            </BrandText>

            <BrandTagline>
              POWERING BUSINESS
            </BrandTagline>

            <BrandDescription>
              Reliable recharge infrastructure and APIs
              designed to help businesses connect,
              automate and scale.
            </BrandDescription>

            <BrandLine />

            <BrandFeatures>
              <BrandFeature>
                <FeatureDot />
                Fast & reliable transactions
              </BrandFeature>

              <BrandFeature>
                <FeatureDot />
                Powerful API infrastructure
              </BrandFeature>

              <BrandFeature>
                <FeatureDot />
                Secure business platform
              </BrandFeature>
            </BrandFeatures>

          </StyledContentInner>

        </StyledContentArea>

      </StyledMainSection>
    </StyledAuthMainOuter>
  );
};

const withAppHOC = (Component) => (props) => {
  const [profile, setProfile] = useState(null);
  const [scrollMessage, setScrollMessage] = useState('');
  const [showNav, setShowNav] = useState(false);

  const readScrollMessage = useCallback(async () => {
    const response = await API.get('/meta/scroll-message');
    const { status, data } = response.data;
    if (status === RESPONSE_STATUSES.SUCCESS) {
      setScrollMessage(data);
    }
  }, []);

  const readProfile = useCallback(async () => {
    const response = await API.get('/authentication/profile');
    const { status, data } = response.data;
    if (status === RESPONSE_STATUSES.SUCCESS) {
      setProfile(data);
      setLocalStorage('userProfile', JSON.stringify(data));
    }
  }, []);

  const onClickLogout = (e) => {
    e.preventDefault();
    API.delete('/authentication/logout');
    clearLocalStorage();
    window.location.href = '/login';
  };

  const onClickNavigationIcon = () => {
    setShowNav((showNav) => !showNav);
  };

  useEffect(() => {
    readProfile();
    readScrollMessage();
  }, [readProfile, readScrollMessage]);

  return (
    <>
      <Header
        fullName={profile?.firstName ? profile.firstName : 'My Account'}
        {...profile}
        onClickLogout={onClickLogout}
        showNav={showNav}
        setShowNav={onClickNavigationIcon}
        {...props}
      />
      <Navigation {...props} showNav={showNav} />
      <ScrollMessage scrollMessage={scrollMessage} />
      <Component {...props} />
      <Footer {...props} />
    </>
  );
};

// const withPublicHOC =
//   (Component) =>
//     ({ appData }) => {
//       return (
//         <>
//           <MetaTags>
//             <link
//               href="https://fonts.googleapis.com/css?family=Poppins:200i,300,300i,400,400i,500,500i,600,600i,700,700i,800,800i,900,900i&display=swap"
//               rel="stylesheet"
//             />

//             <link rel="stylesheet" href="css/bootstrap.min.css" />
//             <link rel="stylesheet" href="css/normalize.css" />
//             <link rel="stylesheet" href="css/style.css" />
//             <link rel="stylesheet" href="css/responsive.css" />
//             <link rel="stylesheet" href="css/color/color1.css" />
//             <link rel="stylesheet" id="colors" />
//           </MetaTags>
//           <PublicHeader {...appData} />
//           <Component {...appData} />
//           <PublicFooter {...appData} />
//           <script src="js/jquery.min.js"></script>
//           <script src="js/bootstrap.min.js"></script>
//           <script src="js/main.js"></script>
//         </>
//       );
//     };

const withPublicHOC = (Component) =>
  ({ appData }) => {
    return (
      <>
        <MetaTags>
          <link
            href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap"
            rel="stylesheet"
          />
        </MetaTags>

        <PublicHeader {...appData} />

        <Component {...appData} />

        <PublicFooter {...appData} />
      </>
    );
  };

export { widthAuthHOC, withAppHOC, withPublicHOC };
